/**
 * Auth Context
 *
 * Manages authentication state across the app using Supabase Auth.
 */

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, signInWithGoogle, signOut as supabaseSignOut, isAllowedEmail, ALLOWED_EMAIL_DOMAINS } from '@/lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  authError: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  // Validate user email domain and sign out if not allowed
  const validateAndSetUser = async (session: Session | null) => {
    if (session?.user) {
      const email = session.user.email || '';
      if (!isAllowedEmail(email)) {
        // Sign out unauthorized user
        await supabaseSignOut();
        setUser(null);
        setSession(null);
        setAuthError(`Access restricted to ${ALLOWED_EMAIL_DOMAINS.map(d => `@${d}`).join(', ')} email addresses only`);
        return;
      }
    }
    setAuthError(null);
    setSession(session);
    setUser(session?.user ?? null);
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      validateAndSetUser(session).then(() => setIsLoading(false));
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        await validateAndSetUser(session);
        setIsLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signIn = useCallback(async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await supabaseSignOut();
      setUser(null);
      setSession(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token ?? null;
    } catch (error) {
      console.error('Get access token error:', error);
      return null;
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders in consumers
  const value = useMemo<AuthContextType>(() => ({
    user,
    session,
    isLoading,
    isAuthenticated: !!user,
    authError,
    signIn,
    signOut,
    getAccessToken,
  }), [user, session, isLoading, authError, signIn, signOut, getAccessToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
